//#region Imports Packages and Libs

import { Component, Prop, Vue } from "vue-facing-decorator";
import FormFooter from "../../components/forms/form_footer.vue";
import FormHeader from "../../components/forms/form_header.vue";
import FormItems from "../../components/forms/form_items.vue";
import { __vueEvent } from "../../constant";
import { EVENTS, STORE_MUTATION } from "../../enums";
import { store } from "../../store/store";
import type { MessageStructureComponent } from "../../types";
import { Util } from "../../util";

//#endregion

//#region Define the component in class-style

@Component({
	components: { FormHeader, FormFooter, FormItems },
})
export default class FormContainer extends Vue {
	//#region Class properties will be component data

	@Prop() component?: MessageStructureComponent;
	@Prop() messageId?: string;
	formValid: boolean = false;

	//#endregion

	//#region Computed Properties

	get prefixClassName() {
		return this.$store.state.prefixClassName || "";
	}

	//#endregion

	//#region Lifecycle Hooks

	mounted() {
		this.listenEvents();
		this.validateForm(); // Validate on mount
		this.setFormButtonOptions();
	}

	//#endregion

	//#region Main Methods

	/**
	 * This method listens for events emitted by the Vue instance.
	 */
	listenEvents() {
		__vueEvent.on(EVENTS.ValidationChange, () => {
			this.validateForm();
		});
	}

	/**
	 * Validates the form by checking the validity of each form item.
	 *
	 * @remarks
	 * This method validates the form by iterating over each form item and performing the following checks:
	 * - Checks if the component and formItems exist.
	 * - Checks if the item is required and if it has a value.
	 * - Performs additional validation for 'number_spinner' type items by checking the value range.
	 *
	 * @returns A boolean value indicating whether the form is valid or not.
	 */
	validateForm() {
		try {
			// Validate first that component & formItems exist
			if (!this.component || !this.component.formItems || !Array.isArray(this.component.formItems)) {
				this.formValid = false;
				return;
			}

			this.formValid = this.component.formItems.every((item) => {
				// Check if isRequired property exists in the item, else assume it is not required
				if (item.isRequired === undefined) {
					item.isRequired = false;
				}

				// Check if the value property exists in the item
				if (item.value === undefined) {
					return false;
				}

				// If the item is required, validate for non-null and non-empty values
				if (item.isRequired && Util.isNullOrEmpty(item.value)) {
					return false;
				}

				// Additional validation for 'number_spinner' type to check value range
				if (item.isRequired && item.type === "number_spinner") {
					const value = Number(item.value);
					if (isNaN(value) || value < item.minValue || value > item.maxValue) {
						return false;
					}
				}

				return true; // Passes validation
			});
		} catch (error) {
			//console.error('Validation Error:', error);
			this.formValid = false;
		}
	}

	/**
	 * Checks if a property exists in the component and if it is an array with at least one element.
	 *
	 * @param propName - The name of the property to check.
	 * @returns A boolean indicating whether the property exists and is an array with at least one element.
	 */
	getPropertyExists(propName: string): boolean {
		try {
			if (!this.component) return false;
			const prop = this.component[propName];
			if (!prop) return false;
			if (!Array.isArray(prop)) return false;
			return prop.length > 0;
		} catch (error) {
			//console.error(`Error checking ${propName}:`, error);
			return false;
		}
	}

	/**
	 * This method is used to check if the form header should be shown.
	 */
	get shouldShowFormHeader() {
		return this.getPropertyExists("formHeader");
	}

	get shouldShowFormFooter() {
		return this.getPropertyExists("formFooter");
	}

	get shouldShowFormItems() {
		return this.getPropertyExists("formItems");
	}

	setFormButtonOptions() {
		try {
			// Once the modal form is mounted, we need to set the button options
			// like messageId which is used to identify the message in the store on button click.
			//store.commit(STORE_MUTATION.SetFormItemChange, { messageId: this.messageId });
			store.commit(STORE_MUTATION.SetFormButtonOptions, { messageId: this.messageId });
		} catch (error) {
			//console.error(`Error checking ${propName}:`, error);
		}
	}

	//#endregion
}

//#endregion
